import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
  useRef,
} from "react";
import { useStore } from "@/store/store";
import { useI18n } from "@/contexts/i18nContext";
import { useToast } from "@/hooks/use-toast";
import SessionExpiryModal from "@/components/session-expiry-modal/SessionExpiryModal";
import { useRefreshTokenMutation } from "@/api-services/login/refresh-token";
import { useUserLogoutMutation } from "@/api-services/login/user-logout";
import {
  SESSION_WARNING_TIME_MS,
  TIMER_INTERVAL_MS,
} from "@/common/config/constants";

interface SessionContextProps {
  expirationTime: number;
  isModalOpen: boolean;
  handleExtendSession: () => void;
  onSessionExtended: (data: {
    accessToken: string;
    refreshToken?: string;
  }) => void;
  onSessionExtendFailed: (error: any) => void;
  isLoading: boolean;
  setIsModalOpen: (open: boolean) => void;
  dismissManually: () => void;
}

type SessionExpiryProviderProps = {
  children: ReactNode;
};

const SessionContext = createContext<SessionContextProps | undefined>(
  undefined,
);

export const useSessionContext = () => {
  const context = useContext(SessionContext);
  if (!context)
    throw new Error("useSessionContext must be used within a SessionProvider");
  return context;
};

export const SessionProvider: React.FC<SessionExpiryProviderProps> = ({
  children,
}) => {
  const { t } = useI18n();
  const { toast } = useToast();
  const auth = useStore((state) => state?.auth);
  const refreshTokenMutation = useRefreshTokenMutation();
  const logoutMutation = useUserLogoutMutation();

  const [expirationTime, setExpirationTime] = useState(0);
  // Controls visibility of the session expiry modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Interval reference for countdown timer
  const timerRef = useRef<number | null>(null);
  /**
   * Tracks whether user manually dismissed the modal.
   * useRef is used to avoid race conditions with the 1s timer.
   */
  const manualDismissRef = useRef(false);
  /**
   * Called when user closes the modal manually.
   * Modal will not reopen again for the same session.
   */
  const dismissManually = () => {
    manualDismissRef.current = true;
    setIsModalOpen(false);
  };

  /**
   * Handles logout:
   * - Clears local auth state
   * - Calls backend logout if refresh token exists
   */
  const handleLogout = () => {
    // Call logout mutation only if refresh token exists
    auth?.logout();
    if (auth?.state?.refreshToken) {
      logoutMutation.mutate({ refreshToken: auth?.state?.refreshToken });
    }
  };

  /**
   * Updates remaining session time every second.
   * - Opens modal when < 3 minutes remaining (if not dismissed)
   * - Logs out user when session expires
   */
  const updateTimer = () => {
    if (!auth?.state?.exp) return;

    const expiryTimestamp = auth?.state?.exp * TIMER_INTERVAL_MS;
    const timeLeft = expiryTimestamp - Date.now();
    setExpirationTime(timeLeft > 0 ? timeLeft : 0);

    if (
      timeLeft <= SESSION_WARNING_TIME_MS &&
      timeLeft > 0 &&
      !manualDismissRef.current &&
      !isModalOpen
    ) {
      setIsModalOpen(true);
    }

    if (timeLeft <= 0) {
      setIsModalOpen(false);
      if (timerRef.current) clearInterval(timerRef.current);
      handleLogout();
    }
  };
  /**
   * Reset manual dismiss flag whenever a new session starts
   * (new login or token refresh)
   */
  useEffect(() => {
    manualDismissRef.current = false;
  }, [auth?.state?.exp]);
  /**
   * Starts the session countdown timer
   * Cleans up interval on unmount or exp change
   */
  useEffect(() => {
    if (!auth?.state?.exp || manualDismissRef.current) return;

    updateTimer(); // Call the updateTimer function only when the expiration time changes.
    timerRef.current = window.setInterval(updateTimer, TIMER_INTERVAL_MS);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [auth?.state?.exp]);

  /**
   * Called when refresh token succeeds
   * - Updates auth tokens
   * - Closes modal
   * - Resets manual dismiss flag
   */
  const onSessionExtended = (data: {
    accessToken: string;
    user: {
      username: string;
      email: string;
      role: string;
    };
    refreshToken?: string;
  }) => {
    auth?.refreshSession(data?.accessToken, data?.user, data?.refreshToken);
    setIsModalOpen(false);
    manualDismissRef.current = false; // <-- reset manual dismiss
  };

  /**
   * Called when refresh token fails
   * - Shows error toast
   * - Logs user out
   */
  const onSessionExtendFailed = (error: any) => {
    toast({
      title: t("toastMessages.sessionExpired"),
      description: t("toastMessages.sessionExpired.description"),
      variant: "destructive",
    });
    handleLogout();
  };

  /**
   * Triggers refresh token API call
   */
  const handleExtendSession = () => {
    if (!auth.state.refreshToken) {
      onSessionExtendFailed("No refresh token");
      return;
    }

    refreshTokenMutation.mutate(
      { refreshToken: auth?.state?.refreshToken },
      {
        onSuccess: (response) => {
          const data = response?.data;
          onSessionExtended({
            accessToken: data?.data?.accessToken,
            user: data?.data?.user,
            refreshToken: data?.data?.refreshToken,
          });
        },
        onError: (error) => onSessionExtendFailed(error),
      },
    );
  };

  return (
    <SessionContext.Provider
      value={{
        expirationTime,
        isModalOpen,
        handleExtendSession,
        onSessionExtended,
        onSessionExtendFailed,
        isLoading: refreshTokenMutation.isPending,
        setIsModalOpen,
        dismissManually,
      }}
    >
      {children}
      <SessionExpiryModal />
    </SessionContext.Provider>
  );
};
